import { IHighlight, NewHighlight } from "react-pdf-highlighter";
import { create } from "zustand";
import { produce } from "immer";
import { devtools } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";
import { ExtendedSectionHighlight } from "../animation/types";
import { v4 as uuidv4 } from "uuid";
import { TDetailedStoryboard } from "pages/media/types";
import { useContext } from "react";
import { ProjectContext } from "providers/ProjectProvider/";

type Selector = { start: string; text: string };

export type HighlightType = "pdf" | "html";
type BaseHighlight = {
  id: string;
  generated?: boolean;
  type?: HighlightType;
  broken?: boolean;
}; // not filled for legacy model
export type HtmlHighlight = BaseHighlight & {
  selectors: Selector[];
};
export type PdfHighlight = BaseHighlight & IHighlight; // & { type: "pdf" };
export type Highlight = PdfHighlight | HtmlHighlight;
// not perfect mixed array type but easier for now
export type Highlights = (PdfHighlight | HtmlHighlight)[];
export type NewHtmlHighlight = Omit<HtmlHighlight, "id">;
export type NewPdfHighlight = NewHighlight;

type State = {
  highlights: Highlights;
  highlightIdToSetup?: string;
  currentHighlight?: Highlight;
  isEditing: boolean;
  sectionHighlights: ExtendedSectionHighlight[];
  // specific in this store to not confuse with the TDetailedStoryboard of the storyboard store
  // we only need a Storyboard for the animation editor
  // (containing highlighting tags)
  highlightedStoryboard?: TDetailedStoryboard;
};

type Action = {
  setHighlights: (highlights: State["highlights"]) => void;
  setHighlightIdToSetup: (
    highlightIdToSetup: State["highlightIdToSetup"]
  ) => void;
  setCurrentHighlight: (currentHighlight: State["currentHighlight"]) => void;
  updateHighlight: (
    highlightId: string,
    position: {
      x1?: number;
      y1?: number;
      x2?: number;
      y2?: number;
      left?: number;
      top?: number;
    },
    content: {
      text: string;
      image?: string;
      scriptText?: string;
    }
  ) => void;
  addHighlight: (highlight: NewPdfHighlight | NewHtmlHighlight) => Highlight;
  deleteHighlight: (highlightId?: string) => void;
  deleteAllHighlights: () => void;
  setIsEditing: (isEditing: State["isEditing"]) => void;
  setSectionHighlights: (sectionHighlights: State["sectionHighlights"]) => void;
  deleteSectionHighlight: (highlightId: string) => void;
  deleteAllSectionHighlight: () => void;
  setHighlightedStoryboard: (
    highlightedStoryboard: State["highlightedStoryboard"]
  ) => void;
  markHighlightAsBroken: (highlightId: string) => void;
  resetHighlightsStore: () => void;
};

const initialState: State = {
  highlights: [],
  highlightIdToSetup: "",
  currentHighlight: undefined,
  isEditing: false,
  sectionHighlights: [],
  highlightedStoryboard: undefined,
};

export const createHighlightsStore = () =>
  create<State & Action>()(
    devtools(
      immer((set) => ({
        ...initialState,
        setIsEditing: (isEditing) => set(() => ({ isEditing })),
        setHighlights: (highlights) => set(() => ({ highlights })),
        setHighlightIdToSetup: (highlightIdToSetup) =>
          set(() => ({ highlightIdToSetup })),
        setCurrentHighlight: (currentHighlight) => {
          if (currentHighlight) {
            set((state) => ({
              currentHighlight: state.highlights.find(
                (highlight) => highlight.id === currentHighlight.id
              ),
            }));
          } else {
            set(() => ({ currentHighlight }));
          }
        },
        updateHighlight: (highlightId, positionUpdate, contentUpdate) =>
          set(
            produce((state) => {
              const highlight = state.highlights.find(
                (h) => h.id === highlightId
              );
              if (!highlight) return;

              highlight.position = {
                ...highlight.position,
                ...positionUpdate,
              };
              highlight.content = {
                ...highlight.content,
                ...contentUpdate,
              };
            })
          ),
        addHighlight: (highlight) => {
          const id = uuidv4();
          const newHighlight = {
            ...highlight,
            id: ("id" in highlight ? highlight.id : id) as string,
          };

          set((state) => {
            state.setCurrentHighlight(newHighlight);
            return { highlights: [...state.highlights, newHighlight] };
          });

          return newHighlight;
        },
        deleteHighlight: (highlightId) =>
          set(
            produce((state) => {
              state.highlights = state.highlights.filter(
                (highlight) => highlight.id !== highlightId
              );
            })
          ),
        deleteAllHighlights: () => set(() => ({ highlights: [] })),
        setSectionHighlights: (sectionHighlights) =>
          set(() => ({ sectionHighlights })),
        deleteSectionHighlight: (highlightId) =>
          set(
            produce((state) => {
              state.sectionHighlights = state.sectionHighlights.filter(
                (highlight) => highlight.sourceId !== highlightId
              );
            })
          ),
        deleteAllSectionHighlight: () => set(() => ({ sectionHighlights: [] })),
        setHighlightedStoryboard: (highlightedStoryboard) =>
          set(() => ({ highlightedStoryboard })),
        markHighlightAsBroken: (highlightId) =>
          set(
            produce((state) => {
              const highlight = state.highlights.find(
                (h) => h.id === highlightId
              );
              if (highlight) {
                highlight.broken = true;
              }
            })
          ),
        resetHighlightsStore: () => {
          set(initialState);
        },
      }))
    )
  );

//Hook to access highlight state with a selector
export const useHighlightsStore = (
  selector?: (state: State & Action) => State & Action
) => {
  const context = useContext(ProjectContext);
  if (!context) {
    throw new Error("useHighlightsStore must be used within ProjectProvider");
  }
  const store = context.highlightsStore;
  return store(selector);
};
